import { useMemo, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useDeliveryFiltersOptional } from '@/services/db/delivery/queries';
import { orderedMessagesFor, withDeletedPlaceholder, type GroupingLabels } from './message-grouping';
import { segmentablePickerData, type PickerMessage } from './message-picker-data';
import {
  computeHasMoreResults,
  ensureSelectedOptionPresent,
  mergeGroupedOptions,
  virtualQuery,
  type MessageSelectOption,
} from './message-select-pagination';

const LEGACY_SMS_TYPE = 'twilio';
const SMS_MESSAGE_CHANNEL_TYPE = 'sms';

type MessageSelectProps = {
  channel: string;
  /**
   * The selected message value in the picker grammar ('*', 'push_*',
   * 'output_11', 'newsletter_3', 'any_email-campaign_4', ...). An empty
   * string means no message has been picked yet.
   */
  value: string;
  /** Whether the plan allows anonymous in-app broadcast entries. */
  allowInAppBroadcasts: boolean;
  disabled?: boolean;
  /** A row-owned validation message; takes precedence over the picker's own deleted-message error. */
  error?: string;
  onChange: (eventName: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

function isAnyValue(value: string): boolean {
  return value === '*' || value.endsWith('_*') || value.startsWith('any_');
}

function useGroupingLabels(): GroupingLabels {
  const taxonomy = useProductTaxonomy();
  return useMemo(
    () => ({
      campaignNoun: taxonomy.term('campaigns.singular'),
      campaignsPlural: taxonomy.term('campaigns.titlePlural'),
      newslettersPlural: taxonomy.term('broadcasts.titlePlural'),
    }),
    [taxonomy],
  );
}

function useOrderedMessages(channel: string, value: string, allowInAppBroadcasts: boolean, labels: GroupingLabels) {
  const filters = useDeliveryFiltersOptional();
  const pickerData = useMemo(() => (filters.data == null ? null : segmentablePickerData(filters.data)), [filters.data]);

  // Legacy Twilio conditions can also target messages from the newer SMS channel.
  const segmentableTypes = useMemo(
    () => (channel === LEGACY_SMS_TYPE ? [LEGACY_SMS_TYPE, SMS_MESSAGE_CHANNEL_TYPE] : [channel]),
    [channel],
  );

  const isDeleted = useMemo(() => {
    if (pickerData == null || value === '' || isAnyValue(value)) {
      return false;
    }
    return !pickerData.segmentable.some(
      (message) => segmentableTypes.includes(message.type) && message.output_id === value,
    );
  }, [pickerData, value, segmentableTypes]);

  const orderedMessages: PickerMessage[] = useMemo(() => {
    if (pickerData == null) {
      return [];
    }
    const ordered = orderedMessagesFor(
      pickerData.segmentable,
      {
        types: segmentableTypes,
        type: channel,
        // Per-variation entries render only where a surface opts in — the
        // people-search picker excludes them.
        includeVariations: false,
        includeLanguageBranches: true,
        allowWildcardSelection: true,
        allowInParentWildcardSelection: true,
        allowInAppBroadcastSelection: allowInAppBroadcasts,
      },
      labels,
    );
    return withDeletedPlaceholder(ordered, value, isDeleted, channel);
  }, [pickerData, segmentableTypes, channel, allowInAppBroadcasts, labels, value, isDeleted]);

  return { orderedMessages, isDeleted, isLoadingCatalog: filters.isLoading };
}

/**
 * The message picker: "any {channel}" wildcards, per-campaign wildcards, and
 * specific campaign/broadcast/newsletter/transactional messages, grouped by
 * their parent. The workspace's whole segmentable list is in memory, so
 * search and load-more page it client-side.
 */
export function MessageSelect({
  channel,
  value,
  allowInAppBroadcasts,
  disabled,
  error,
  onChange,
  onOpenChange,
}: MessageSelectProps) {
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const labels = useGroupingLabels();
  const { orderedMessages, isDeleted, isLoadingCatalog } = useOrderedMessages(
    channel,
    value,
    allowInAppBroadcasts,
    labels,
  );

  const selectedMessage = useMemo(
    () => orderedMessages.find((message) => message.output_id === value) ?? null,
    [orderedMessages, value],
  );

  const options: MessageSelectOption[] = useMemo(() => {
    let merged = virtualQuery(orderedMessages, debouncedSearch, 1, labels);
    for (let nextPage = 2; nextPage <= page; nextPage += 1) {
      merged = mergeGroupedOptions(merged, virtualQuery(orderedMessages, debouncedSearch, nextPage, labels));
    }
    return ensureSelectedOptionPresent(merged, selectedMessage);
  }, [orderedMessages, debouncedSearch, page, labels, selectedMessage]);

  const hasMoreResults = computeHasMoreResults(orderedMessages, debouncedSearch, page);

  const errorMessage = error ?? (isDeleted ? 'The selected message has been deleted' : undefined);

  const handleSelect = (selected: string | null) => {
    if (selected == null) {
      return;
    }
    const item = orderedMessages.find((message) => message.output_id === selected);
    if (item == null) {
      return;
    }
    // Language-branch entries ("All Variations") carry a synthetic output_id;
    // the selectable value is their action id.
    onChange(item.isLanguageBranch === true && item.id != null ? `output_${item.id}` : selected);
  };

  return (
    <Select
      data-testid="dryad-message-filter-select"
      ariaLabel="Message"
      size="sm"
      placeholder="Select a message"
      // The default renderer lets this nested grouped menu grow to the whole
      // viewport width; bound it so the dropdown anchors under the trigger
      // like the picker it replaces (verified live at this width).
      unsafe_popoverSize={{ maxWidth: 480 }}
      options={options}
      value={selectedMessage?.output_id}
      isSearchable
      searchPlaceholder="Search…"
      searchInputValue={searchInput}
      onSearchInput={(event) => {
        setSearchInput(event.target.value);
        setPage(1);
      }}
      onLoadMore={() => setPage((current) => current + 1)}
      shouldLoadMore={hasMoreResults}
      isLoading={isLoadingCatalog || searchInput !== debouncedSearch}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          setSearchInput('');
          setPage(1);
        }
        onOpenChange?.(isOpen);
      }}
      onChange={handleSelect}
      error={errorMessage}
      isDisabled={disabled === true || isLoadingCatalog}
    />
  );
}
